import { useState } from 'react';
import { Link } from 'react-router-dom';
import { OpsApiError } from '../api/client.ts';
import { useEgressPolicies, useOrgEgressPolicy, usePutOrgEgressPolicy } from '../api/useEgressPolicies.ts';
import { ROLE_OPS_ADMIN, hasAnyOpsRole } from '../auth/opsRoles.ts';
import { useSession } from '../hooks/useSession.tsx';
import { useT } from '../i18n/index.ts';
import { customerEmailOrNull, customerIdentity } from './customer-identity.ts';
import { Button, Chip, ConfirmDialog, Panel, Select, Skeleton } from '../ui/index.ts';
import type { SelectOption } from '../ui/index.ts';
import { useToast, coerceErrorMessage } from './Toast.tsx';

const DEFAULT_OPTION = 'default';

export function CustomerEgressPolicyCard({
  orgId,
  email,
  testId,
}: {
  readonly orgId: string;
  readonly email?: string | null;
  readonly testId: string;
}): JSX.Element {
  const t = useT();
  const toast = useToast();
  const { user } = useSession();
  const isAdmin = hasAnyOpsRole(user?.roles ?? [], [ROLE_OPS_ADMIN]);
  const assignment = useOrgEgressPolicy(orgId);
  const catalogue = useEgressPolicies(isAdmin);
  const putMut = usePutOrgEgressPolicy();
  const [draft, setDraft] = useState<string | null>(null);
  const [confirmUnassign, setConfirmUnassign] = useState(false);
  const [confirmAssign, setConfirmAssign] = useState(false);

  if (assignment.isLoading || (isAdmin && catalogue.isLoading)) {
    return (
      <Panel caption={t('customer_manager_360.egress_policy.caption')} padded testId={testId}>
        <div data-testid={`${testId}-loading`} className="flex flex-col gap-3">
          <Skeleton className="h-9 w-full max-w-sm" />
          <Skeleton className="h-4 w-2/3" />
        </div>
      </Panel>
    );
  }

  if (assignment.data === undefined) {
    return (
      <Panel caption={t('customer_manager_360.egress_policy.caption')} padded testId={testId}>
        <p className="m-0 text-dense text-bad" role="alert" data-testid={`${testId}-error`}>
          {t('customer_manager_360.egress_policy.error')}
        </p>
      </Panel>
    );
  }

  const policy = assignment.data.policy;
  const assigned = policy !== null && !policy.is_global_default;
  const currentId = assigned ? policy.id : DEFAULT_OPTION;
  const selection = draft ?? currentId;
  const overrideCount = assigned ? policy.rules.length : 0;
  const options: SelectOption[] = [
    { value: DEFAULT_OPTION, label: t('customer_manager_360.egress_policy.global') },
    ...(catalogue.data?.policies ?? [])
      .filter((row) => !row.is_global_default)
      .map((row) => ({ value: row.id, label: row.name })),
  ];

  async function save(policyId: string | null): Promise<void> {
    try {
      await putMut.mutateAsync({ orgId, body: { policy_id: policyId } });
      setDraft(null);
      setConfirmAssign(false);
      setConfirmUnassign(false);
      toast.success(t('customer_manager_360.egress_policy.saved'));
    } catch (error) {
      toast.error(error instanceof OpsApiError ? error.message : coerceErrorMessage(error));
    }
  }

  const customerLabel = customerIdentity(orgId, email);
  const selectedPolicyName = selection === DEFAULT_OPTION
    ? t('customer_manager_360.egress_policy.global')
    : (catalogue.data?.policies ?? []).find((row) => row.id === selection)?.name ?? selection;

  return (
    <Panel caption={t('customer_manager_360.egress_policy.caption')} padded testId={testId}>
      <div className="flex flex-col gap-3">
        <p className="m-0 font-mono text-dense text-paper" data-testid={`${testId}-customer`}>
          {customerEmailOrNull(email) !== null ? (
            customerLabel
          ) : (
            <>
              {orgId}
              <span className="ml-2 text-paper-low">{t('customer_manager_360.egress_policy.no_email')}</span>
            </>
          )}
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-dense text-paper" data-testid={`${testId}-name`}>
            {assigned ? policy.name : t('customer_manager_360.egress_policy.global')}
          </span>
          {assigned ? (
            <Chip tone="neutral" lamp={false}>v{policy.version}</Chip>
          ) : (
            <Chip tone="signal" lamp={false}>{t('customer_manager_360.egress_policy.global_badge')}</Chip>
          )}
        </div>
        <p className="m-0 text-dense text-paper-low" data-testid={`${testId}-overrides`}>
          {t('customer_manager_360.egress_policy.overrides', { count: String(overrideCount) })}
        </p>
        {isAdmin ? (
          <div className="flex flex-wrap items-center gap-2">
            <Select
              value={selection}
              options={options}
              onValueChange={setDraft}
              testId={`${testId}-select`}
            />
            <Button
              size="sm"
              disabled={selection === currentId}
              loading={putMut.isPending}
              onClick={() => {
                if (selection === DEFAULT_OPTION) setConfirmUnassign(true);
                else setConfirmAssign(true);
              }}
              data-testid={`${testId}-save`}
            >
              {t('customer_manager_360.egress_policy.change')}
            </Button>
            {assigned ? (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setConfirmUnassign(true)}
                data-testid={`${testId}-unassign`}
              >
                {t('customer_manager_360.egress_policy.unassign')}
              </Button>
            ) : null}
          </div>
        ) : null}
        {isAdmin ? (
          <Link
            className="text-dense text-signal"
            to="/supply/egress-lines?tab=policies"
            data-testid={`${testId}-link`}
          >
            {t('customer_manager_360.egress_policy.link')}
          </Link>
        ) : null}
      </div>
      <ConfirmDialog
        open={confirmAssign}
        onOpenChange={(open) => { if (!open) setConfirmAssign(false); }}
        title={t('customer_manager_360.egress_policy.assign_confirm_title')}
        body={t('customer_manager_360.egress_policy.assign_confirm_body', {
          customer: customerLabel,
          policy: selectedPolicyName,
        })}
        confirmLabel={t('customer_manager_360.egress_policy.change')}
        cancelLabel={t('common.cancel')}
        closeLabel={t('common.close')}
        loading={putMut.isPending}
        onConfirm={() => {
          void save(selection === DEFAULT_OPTION ? null : selection);
        }}
        testId={`${testId}-assign-confirm`}
      />
      <ConfirmDialog
        open={confirmUnassign}
        onOpenChange={(open) => { if (!open) setConfirmUnassign(false); }}
        title={t('customer_manager_360.egress_policy.unassign')}
        body={t('customer_manager_360.egress_policy.unassign_body', {
          customer: customerLabel,
          policy: assigned ? policy.name : t('customer_manager_360.egress_policy.global'),
        })}
        confirmLabel={t('customer_manager_360.egress_policy.unassign')}
        cancelLabel={t('common.cancel')}
        closeLabel={t('common.close')}
        loading={putMut.isPending}
        onConfirm={() => {
          void save(null);
        }}
        testId={`${testId}-unassign-confirm`}
      />
    </Panel>
  );
}
